import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate, useParams } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import {
  ArrowLeft,
  Calendar,
  MapPin,
  Users,
  Upload,
  Save,
  DollarSign,
  Plus,
  X,
  HelpCircle,
} from 'lucide-react'
import { LoadingInline } from '@/components/ui/spinner'
import type { Perfil, PapelUsuario } from '@/types'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Checkbox } from '@/components/ui/checkbox'
import { UserCog } from 'lucide-react'
import type { EquipeMember } from './evento-detalhe/types'
// import { getEventoSlug } from '@/lib/eventos'
import { useEvento, useEventoEquipe, useAtualizarEvento } from '@/hooks/use-eventos'
import { useMembros } from '@/hooks/use-membros'
import { useAlert } from '@/hooks/use-alert'

interface Pergunta {
  id: string
  texto: string
  tipo: 'texto' | 'multipla_escolha' | 'sim_nao'
  obrigatoria: boolean
}

interface EventoFormData {
  titulo: string
  descricao: string
  tipo: string
  inicio_em: string
  termino_em: string
  local: string
  capacidade: string
  inscricoes_abertas: boolean
  status: string
  gratuito: boolean
  valor: string
  valor_inscricao: string
  formasPagamento: string[]
  visibilidade: 'interno' | 'externo'
  limitar_vagas_externas: boolean
  vagas_externas: string
  chave_pix: string
  permitir_parcelamento: boolean
  numero_parcelas: string
}

const FORMAS_PAGAMENTO_OPCOES = [
  { value: 'pix', label: 'Pix' },
  { value: 'dinheiro', label: 'Dinheiro' },
  { value: 'cartao', label: 'Cartão' },
]

export function EventosEditarPage() {
  const navigate = useNavigate()
  const { eventoSlug } = useParams<{ eventoSlug: string }>()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState<EventoFormData | null>(null)
  const [perguntas, setPerguntas] = useState<Pergunta[]>([])
  const [equipe, setEquipe] = useState<EquipeMember[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState<Perfil[]>([])

  // Buscar evento e equipe do banco de dados
  const { data: eventoData, isLoading: isLoadingEvento } = useEvento(eventoSlug)
  const { data: equipeData, isLoading: isLoadingEquipe } = useEventoEquipe(eventoData?.id)
  const { data: membrosData } = useMembros()

  // Mutation para atualizar evento
  const atualizarEvento = useAtualizarEvento()
  const { alert } = useAlert()

  useEffect(() => {
    if (eventoData) {
      setFormData({
        titulo: eventoData.titulo || '',
        descricao: eventoData.descricao || '',
        tipo: eventoData.tipo || '',
        inicio_em: eventoData.inicio_em ? new Date(eventoData.inicio_em).toISOString().slice(0, 16) : '',
        termino_em: eventoData.fim_em ? new Date(eventoData.fim_em).toISOString().slice(0, 16) : '',
        local: eventoData.local || '',
        capacidade: eventoData.capacidade?.toString() || '',
        inscricoes_abertas: eventoData.inscricoes_abertas || false,
        status: eventoData.status || 'rascunho',
        gratuito: eventoData.gratuito || eventoData.valor === 0,
        valor: eventoData.valor?.toString() || '0',
        valor_inscricao: eventoData.valor_inscricao?.toString() || '0',
        formasPagamento: eventoData.formas_pagamento || [],
        visibilidade: eventoData.visibilidade || 'interno',
        limitar_vagas_externas: eventoData.limitar_vagas_externas || false,
        vagas_externas: eventoData.vagas_externas?.toString() || '',
        chave_pix: eventoData.chave_pix || '',
        permitir_parcelamento: eventoData.permitir_parcelamento || false,
        numero_parcelas: eventoData.numero_parcelas?.toString() || '',
      })
    }
  }, [eventoData])

  useEffect(() => {
    if (equipeData) {
      setEquipe(equipeData)
    }
  }, [equipeData])

  const handleSearchTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value
    setSearchTerm(term)

    if (term.trim() === '' || !membrosData) {
      setSearchResults([])
    } else {
      const filtered = membrosData
        .map((m) => ({
          id: m.id,
          user_id: m.auth_user_id,
          nome_exibicao: m.nome,
          avatar_url: null, // usuarios não tem avatar_url
          telefone: m.telefone,
          papel: (m.permissao || 'comum') as PapelUsuario,
          igreja_id: m.igreja_id,
          created_at: m.criado_em,
          updated_at: m.atualizado_em,
        }))
        .filter(
          (membro) =>
            membro.nome_exibicao.toLowerCase().includes(term.toLowerCase()) &&
            !equipe.some((e) => e.id === membro.id)
        )
      setSearchResults(filtered)
    }
  }

  const adicionarMembro = (membro: Perfil) => {
    setEquipe([
      ...equipe,
      {
        id: membro.id,
        nome_exibicao: membro.nome_exibicao,
        avatar_url: membro.avatar_url,
        permissoes: {
          editar_evento: false,
          confirmar_pagamento: false,
          excluir_evento: false,
        },
      },
    ])
    setSearchTerm('')
    setSearchResults([])
  }

  const removerMembro = (id: string) => {
    setEquipe(equipe.filter((m) => m.id !== id))
  }

  const togglePermissao = (id: string, permissao: keyof EquipeMember['permissoes']) => {
    setEquipe(
      equipe.map((m) =>
        m.id === id
          ? { ...m, permissoes: { ...m.permissoes, [permissao]: !m.permissoes[permissao] } }
          : m
      )
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!eventoData || !formData) return

    if (equipe.length === 0) {
      alert.error('Erro', 'A equipe do evento não pode estar vazia.')
      return
    }

    setIsSubmitting(true)

    try {
      // Preparar dados para atualização
      const eventoUpdate = {
        titulo: formData.titulo,
        descricao: formData.descricao || null,
        tipo: (formData.tipo || undefined) as import('@/types/database').Database['public']['Enums']['tipo_evento'] | undefined,
        inicio_em: formData.inicio_em ? new Date(formData.inicio_em).toISOString() : undefined,
        fim_em: formData.termino_em ? new Date(formData.termino_em).toISOString() : undefined,
        local: formData.local || undefined,
        capacidade: formData.capacidade ? parseInt(formData.capacidade) : undefined,
        inscricoes_abertas: formData.inscricoes_abertas,
        status: (formData.status || null) as import('@/types/database').Database['public']['Enums']['status_evento'] | null,
        gratuito: formData.gratuito,
        valor: formData.gratuito ? 0 : parseFloat(formData.valor) || 0,
        valor_inscricao: formData.gratuito ? 0 : (formData.valor_inscricao ? parseFloat(formData.valor_inscricao) : 0),
        formas_pagamento: formData.gratuito ? null : formData.formasPagamento,
        chave_pix: formData.formasPagamento.includes('pix') ? formData.chave_pix : null,
        visibilidade: (formData.visibilidade || null) as import('@/types/database').Database['public']['Enums']['visibilidade_evento'] | null,
        limitar_vagas_externas: formData.limitar_vagas_externas,
        vagas_externas: formData.limitar_vagas_externas && formData.vagas_externas
          ? parseInt(formData.vagas_externas)
          : null,
        permitir_parcelamento: formData.gratuito ? false : formData.permitir_parcelamento,
        numero_parcelas: formData.permitir_parcelamento && formData.numero_parcelas
          ? parseInt(formData.numero_parcelas)
          : null,
      }

      await atualizarEvento.mutateAsync({
        id: eventoData.id,
        evento: eventoUpdate,
      })

      alert.success('Sucesso!', 'Evento atualizado com sucesso.')
      navigate(`/eventos/${eventoSlug}`)
    } catch (error) {
      console.error('Erro ao atualizar evento:', error)
      alert.error('Erro', 'Não foi possível atualizar o evento. Tente novamente.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (field: keyof EventoFormData, value: string | boolean | string[]) => {
    setFormData((prev) => (prev ? { ...prev, [field]: value } : null))
  }

  const toggleFormaPagamento = (forma: string) => {
    setFormData((prev) => {
      if (!prev) return null
      const jaSelecionada = prev.formasPagamento.includes(forma)
      const formasPagamento = jaSelecionada
        ? prev.formasPagamento.filter((item) => item !== forma)
        : [...prev.formasPagamento, forma]

      return { ...prev, formasPagamento }
    })
  }

  const adicionarPergunta = () => {
    const novaPergunta: Pergunta = {
      id: Date.now().toString(),
      texto: '',
      tipo: 'texto',
      obrigatoria: false,
    }
    setPerguntas([...perguntas, novaPergunta])
  }

  const removerPergunta = (id: string) => {
    setPerguntas(perguntas.filter((p) => p.id !== id))
  }

  const atualizarPergunta = (id: string, campo: keyof Pergunta, valor: string | boolean) => {
    setPerguntas(perguntas.map((p) => (p.id === id ? { ...p, [campo]: valor } : p)))
  }

  // Loading state
  if (isLoadingEvento || isLoadingEquipe) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center gap-2">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <span>Carregando evento...</span>
        </div>
      </div>
    )
  }

  // Not found state
  if (!eventoData || !formData) {
    return (
      <div className="space-y-6 px-4 py-6">
        <p className="text-muted-foreground">Evento não encontrado.</p>
        <Button variant="outline" onClick={() => navigate('/eventos')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar para Eventos
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-4"
      >
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(`/eventos/${eventoSlug}`)}
          className="shrink-0"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Editar Evento</h1>
          <p className="text-muted-foreground">{formData.titulo}</p>
        </div>
      </motion.div>

      {/* Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <form onSubmit={handleSubmit}>
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Main Form */}
            <div className="space-y-6 lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>Informações Básicas</CardTitle>
                  <CardDescription>Dados principais do evento</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="titulo">
                      Título do Evento <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="titulo"
                      placeholder="Ex: Culto de Celebração"
                      value={formData.titulo}
                      onChange={(e) => handleChange('titulo', e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="descricao">Descrição</Label>
                    <Textarea
                      id="descricao"
                      placeholder="Descreva o evento..."
                      rows={4}
                      value={formData.descricao}
                      onChange={(e) => handleChange('descricao', e.target.value)}
                    />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="tipo">
                        Tipo de Evento <span className="text-destructive">*</span>
                      </Label>
                      <Select
                        value={formData.tipo}
                        onValueChange={(value) => handleChange('tipo', value)}
                      >
                        <SelectTrigger id="tipo">
                          <SelectValue placeholder="Selecione o tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="culto">Culto</SelectItem>
                          <SelectItem value="estudo">Estudo Bíblico</SelectItem>
                          <SelectItem value="evento-especial">Evento Especial</SelectItem>
                          <SelectItem value="retiro">Retiro</SelectItem>
                          <SelectItem value="conferencia">Conferência</SelectItem>
                          <SelectItem value="reuniao">Reunião</SelectItem>
                          <SelectItem value="outro">Outro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="status">Status</Label>
                      <Select
                        value={formData.status}
                        onValueChange={(value) => handleChange('status', value)}
                      >
                        <SelectTrigger id="status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="rascunho">Rascunho</SelectItem>
                          <SelectItem value="programado">Programado</SelectItem>
                          <SelectItem value="confirmado">Confirmado</SelectItem>
                          <SelectItem value="publicado">Publicado</SelectItem>
                          <SelectItem value="adiado">Adiado</SelectItem>
                          <SelectItem value="cancelado">Cancelado</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-primary" />
                    Data e Horário
                  </CardTitle>
                  <CardDescription>Quando o evento acontecerá</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="inicio_em">
                        Início <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="inicio_em"
                        type="datetime-local"
                        value={formData.inicio_em}
                        onChange={(e) => handleChange('inicio_em', e.target.value)}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="termino_em">Término</Label>
                      <Input
                        id="termino_em"
                        type="datetime-local"
                        value={formData.termino_em}
                        onChange={(e) => handleChange('termino_em', e.target.value)}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-primary" />
                    Local e Capacidade
                  </CardTitle>
                  <CardDescription>Onde o evento acontecerá</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="local">Local</Label>
                    <Input
                      id="local"
                      placeholder="Ex: Templo Principal, Salão de Eventos..."
                      value={formData.local}
                      onChange={(e) => handleChange('local', e.target.value)}
                    />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="capacidade">Capacidade (vagas)</Label>
                      <Input
                        id="capacidade"
                        type="number"
                        min="0"
                        placeholder="Ex: 100"
                        value={formData.capacidade}
                        onChange={(e) => handleChange('capacidade', e.target.value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="inscricoes_abertas">Inscrições</Label>
                      <Select
                        value={formData.inscricoes_abertas ? 'abertas' : 'fechadas'}
                        onValueChange={(value) =>
                          handleChange('inscricoes_abertas', value === 'abertas')
                        }
                      >
                        <SelectTrigger id="inscricoes_abertas">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="abertas">Abertas</SelectItem>
                          <SelectItem value="fechadas">Fechadas</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="visibilidade">Visibilidade do Evento</Label>
                      <Select
                        value={formData.visibilidade}
                        onValueChange={(value) =>
                          handleChange('visibilidade', value as 'interno' | 'externo')
                        }
                      >
                        <SelectTrigger id="visibilidade">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="interno">Somente Interno</SelectItem>
                          <SelectItem value="externo">Também Externo</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {formData.visibilidade === 'externo' && formData.capacidade && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="space-y-4"
                    >
                      <div className="space-y-2">
                        <Label htmlFor="limitar_vagas_externas">Limitar Vagas Externas?</Label>
                        <Select
                          value={formData.limitar_vagas_externas ? 'sim' : 'nao'}
                          onValueChange={(value) =>
                            handleChange('limitar_vagas_externas', value === 'sim')
                          }
                        >
                          <SelectTrigger id="limitar_vagas_externas">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="sim">Sim</SelectItem>
                            <SelectItem value="nao">Não</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {formData.limitar_vagas_externas && (
                        <div className="space-y-2">
                          <Label htmlFor="vagas_externas">
                            Vagas Destinadas a Participantes Externos
                          </Label>
                          <Input
                            id="vagas_externas"
                            type="number"
                            min="0"
                            placeholder="Ex: 50"
                            value={formData.vagas_externas}
                            onChange={(e) => handleChange('vagas_externas', e.target.value)}
                          />
                        </div>
                      )}
                    </motion.div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-primary" />
                    Valor do Evento
                  </CardTitle>
                  <CardDescription>Informações sobre pagamento</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="gratuito">Tipo de Evento</Label>
                    <Select
                      value={formData.gratuito ? 'gratuito' : 'pago'}
                      onValueChange={(value) =>
                        setFormData((prev) =>
                          prev
                            ? {
                                ...prev,
                                gratuito: value === 'gratuito',
                                valor: value === 'gratuito' ? '' : prev.valor,
                                formasPagamento: value === 'gratuito' ? [] : prev.formasPagamento,
                              }
                            : null
                        )
                      }
                    >
                      <SelectTrigger id="gratuito">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gratuito">Gratuito</SelectItem>
                        <SelectItem value="pago">Pago</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {!formData.gratuito && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="space-y-4"
                    >
                      <div className="space-y-2">
                        <Label htmlFor="valor">
                          Valor (R$) <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="valor"
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Ex: 50.00"
                          value={formData.valor}
                          onChange={(e) => handleChange('valor', e.target.value)}
                          required={!formData.gratuito}
                        />
                      </div>

                      <div className="space-y-2">
                        <p className="text-sm font-medium">Formas de pagamento aceitas</p>
                        <div className="flex flex-wrap gap-2">
                          {FORMAS_PAGAMENTO_OPCOES.map((opcao) => {
                            const selecionada = formData.formasPagamento.includes(opcao.value)

                            return (
                              <Button
                                key={opcao.value}
                                type="button"
                                variant={selecionada ? 'default' : 'outline'}
                                onClick={() => toggleFormaPagamento(opcao.value)}
                                aria-pressed={selecionada}
                                className={cn(
                                  'h-9 rounded-full px-4 text-sm',
                                  selecionada ? 'shadow-md' : ''
                                )}
                              >
                                {opcao.label}
                              </Button>
                            )
                          })}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Selecione todas as opções disponíveis para este evento.
                        </p>
                      </div>

                      {formData.formasPagamento.includes('pix') && (
                        <div className="space-y-2">
                          <Label htmlFor="chave_pix">
                            Chave Pix <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            id="chave_pix"
                            placeholder="Ex: seuemail@email.com ou 000.000.000-00"
                            value={formData.chave_pix}
                            onChange={(e) => handleChange('chave_pix', e.target.value)}
                            required={formData.formasPagamento.includes('pix')}
                          />
                        </div>
                      )}

                      <div className="space-y-2">
                        <Label htmlFor="valor_inscricao">
                          Valor de Inscrição (R$)
                        </Label>
                        <Input
                          id="valor_inscricao"
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Ex: 20.00"
                          value={formData.valor_inscricao}
                          onChange={(e) => handleChange('valor_inscricao', e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground">
                          Valor fixo para reservar/garantir a vaga. Deixe em branco se não houver taxa de inscrição separada.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="permitir_parcelamento">Permitir Parcelamento?</Label>
                        <Select
                          value={formData.permitir_parcelamento ? 'sim' : 'nao'}
                          onValueChange={(value) =>
                            setFormData((prev) => {
                              if (!prev) return prev
                              return {
                                ...prev,
                                permitir_parcelamento: value === 'sim',
                                numero_parcelas: value === 'sim' ? prev.numero_parcelas : '',
                              }
                            })
                          }
                        >
                          <SelectTrigger id="permitir_parcelamento">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="nao">Não</SelectItem>
                            <SelectItem value="sim">Sim</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Permite que participantes paguem o valor total em parcelas mensais (carnê).
                        </p>
                      </div>

                      {formData.permitir_parcelamento && (
                        <div className="space-y-2">
                          <Label htmlFor="numero_parcelas">
                            Número de Parcelas <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            id="numero_parcelas"
                            type="number"
                            min="2"
                            max="36"
                            placeholder="Ex: 6"
                            value={formData.numero_parcelas}
                            onChange={(e) => handleChange('numero_parcelas', e.target.value)}
                            required={formData.permitir_parcelamento}
                          />
                          <p className="text-xs text-muted-foreground">
                            Quantidade de parcelas disponíveis (máximo 36 meses). Um carnê será gerado para cada participante.
                          </p>
                        </div>
                      )}
                    </motion.div>
                  )}

                  <div className="rounded-lg bg-muted/50 p-3">
                    <p className="text-xs text-muted-foreground">
                      O link de inscrição será gerado automaticamente após a criação do evento
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <HelpCircle className="h-5 w-5 text-primary" />
                        Perguntas de Inscrição
                      </CardTitle>
                      <CardDescription>
                        Perguntas que os participantes responderão ao se inscrever
                      </CardDescription>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={adicionarPergunta}>
                      <Plus className="mr-2 h-4 w-4" />
                      Adicionar
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {perguntas.length === 0 ? (
                    <div className="rounded-lg border-2 border-dashed p-8 text-center">
                      <HelpCircle className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        Nenhuma pergunta adicionada ainda
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Clique em "Adicionar" para criar perguntas personalizadas
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {perguntas.map((pergunta, index) => (
                        <motion.div
                          key={pergunta.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="space-y-3 rounded-lg border p-4"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <span className="text-sm font-medium text-muted-foreground">
                              Pergunta {index + 1}
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removerPergunta(pergunta.id)}
                              className="h-8 w-8 p-0"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor={`pergunta-texto-${pergunta.id}`}>
                              Texto da Pergunta
                            </Label>
                            <Input
                              id={`pergunta-texto-${pergunta.id}`}
                              placeholder="Ex: Você possui alguma restrição alimentar?"
                              value={pergunta.texto}
                              onChange={(e) =>
                                atualizarPergunta(pergunta.id, 'texto', e.target.value)
                              }
                            />
                          </div>

                          <div className="grid gap-3 sm:grid-cols-2">
                            <div className="space-y-2">
                              <Label htmlFor={`pergunta-tipo-${pergunta.id}`}>
                                Tipo de Resposta
                              </Label>
                              <Select
                                value={pergunta.tipo}
                                onValueChange={(value) =>
                                  atualizarPergunta(
                                    pergunta.id,
                                    'tipo',
                                    value as 'texto' | 'multipla_escolha' | 'sim_nao'
                                  )
                                }
                              >
                                <SelectTrigger id={`pergunta-tipo-${pergunta.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="texto">Texto Livre</SelectItem>
                                  <SelectItem value="multipla_escolha">Múltipla Escolha</SelectItem>
                                  <SelectItem value="sim_nao">Sim/Não</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor={`pergunta-obrig-${pergunta.id}`}>Obrigatória?</Label>
                              <Select
                                value={pergunta.obrigatoria ? 'sim' : 'nao'}
                                onValueChange={(value) =>
                                  atualizarPergunta(pergunta.id, 'obrigatoria', value === 'sim')
                                }
                              >
                                <SelectTrigger id={`pergunta-obrig-${pergunta.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="sim">Sim</SelectItem>
                                  <SelectItem value="nao">Não</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Upload className="h-5 w-5 text-primary" />
                    Imagem de Capa
                  </CardTitle>
                  <CardDescription>Imagem principal do evento</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex aspect-video items-center justify-center rounded-lg border-2 border-dashed bg-muted/50">
                      <div className="text-center">
                        <Upload className="mx-auto h-8 w-8 text-muted-foreground" />
                        <p className="mt-2 text-sm text-muted-foreground">
                          Clique para fazer upload
                        </p>
                        <p className="text-xs text-muted-foreground">PNG, JPG até 2MB</p>
                      </div>
                    </div>
                    <Button variant="outline" className="w-full" type="button">
                      <Upload className="mr-2 h-4 w-4" />
                      Fazer Upload
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UserCog className="h-5 w-5 text-primary" />
                    Equipe do Evento
                  </CardTitle>
                  <CardDescription>Adicione membros e defina permissões</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="search-member">Adicionar Membro</Label>
                    <Input
                      id="search-member"
                      placeholder="Digite o nome do membro..."
                      value={searchTerm}
                      onChange={handleSearchTermChange}
                    />
                    {searchResults.length > 0 && (
                      <div className="relative">
                        <div className="absolute z-10 mt-1 w-full rounded-md border bg-background shadow-lg">
                          <ul className="max-h-60 overflow-auto py-1">
                            {searchResults.map((membro) => (
                              <li
                                key={membro.id}
                                className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-muted"
                                onClick={() => adicionarMembro(membro)}
                              >
                                <Avatar className="h-8 w-8">
                                  <AvatarImage src={membro.avatar_url || undefined} />
                                  <AvatarFallback>{membro.nome_exibicao.charAt(0)}</AvatarFallback>
                                </Avatar>
                                <span className="text-sm">{membro.nome_exibicao}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-4">
                    {equipe.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        Nenhum membro na equipe ainda.
                      </p>
                    ) : (
                      equipe.map((membro) => (
                        <div key={membro.id} className="flex items-start gap-3">
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={membro.avatar_url || undefined} />
                            <AvatarFallback>{membro.nome_exibicao.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center justify-between">
                              <p className="font-medium">{membro.nome_exibicao}</p>
                              {!membro.criador && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removerMembro(membro.id)}
                                  className="h-7 w-7 p-0"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                              <div className="flex items-center space-x-2">
                                <Checkbox
                                  id={`edit-${membro.id}`}
                                  checked={membro.permissoes.editar_evento}
                                  onCheckedChange={() =>
                                    togglePermissao(membro.id, 'editar_evento')
                                  }
                                  disabled={membro.criador}
                                />
                                <Label
                                  htmlFor={`edit-${membro.id}`}
                                  className="text-xs font-normal"
                                >
                                  Editar
                                </Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Checkbox
                                  id={`payment-${membro.id}`}
                                  checked={membro.permissoes.confirmar_pagamento}
                                  onCheckedChange={() =>
                                    togglePermissao(membro.id, 'confirmar_pagamento')
                                  }
                                  disabled={membro.criador}
                                />
                                <Label
                                  htmlFor={`payment-${membro.id}`}
                                  className="text-xs font-normal"
                                >
                                  Pagamentos
                                </Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Checkbox
                                  id={`delete-${membro.id}`}
                                  checked={membro.permissoes.excluir_evento}
                                  onCheckedChange={() =>
                                    togglePermissao(membro.id, 'excluir_evento')
                                  }
                                  disabled={membro.criador}
                                />
                                <Label
                                  htmlFor={`delete-${membro.id}`}
                                  className="text-xs font-normal"
                                >
                                  Excluir
                                </Label>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="border-primary/20 bg-primary/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-primary" />
                    Dicas
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm text-muted-foreground">
                  <p>Use imagens de alta qualidade para atrair mais participantes.</p>
                  <p>Defina uma capacidade para controlar o número de inscrições.</p>
                  <p>
                    Adicione perguntas personalizadas para coletar informações importantes dos
                    participantes.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Spacer for fixed buttons */}
          <div className="h-20" />
        </form>
      </motion.div>

      {/* Fixed Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="fixed bottom-0 left-0 right-0 z-40 border-t bg-background/95 backdrop-blur-sm lg:left-64"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-end gap-3 px-4 py-4 sm:px-6 lg:px-8">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/eventos/${eventoSlug}`)}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="gap-2"
            onClick={(e) => {
              e.preventDefault()
              const form = document.querySelector('form')
              if (form) {
                form.requestSubmit()
              }
            }}
          >
            {isSubmitting ? (
              <LoadingInline message="Salvando..." size="sm" />
            ) : (
              <>
                <Save className="h-4 w-4" />
                Salvar Alterações
              </>
            )}
          </Button>
        </div>
      </motion.div>
    </div>
  )
}
